import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {ChevronLeft, ChevronRight, Facebook, Instagram, Mail, MapPin, Phone, Send, X} from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../utils/LanguageContext';
import {Helmet} from "react-helmet-async";
import getHelmetConfig from "../utils/helmetConfig";


// API Base URL
const API_BASE_URL = 'https://texla.uz/api/v1';

// Skeleton Loader Components
const SkeletonLoader = ({ className = "" }) => (
    <div className={`animate-pulse bg-gray-200 rounded ${className}`}></div>
);

const ProductSkeleton = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
        <SkeletonLoader className="h-48 w-full" />
        <div className="p-4">
            <SkeletonLoader className="h-4 mb-2" />
            <SkeletonLoader className="h-10 w-full" />
        </div>
    </div>
);

const NewsSkeleton = () => (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <SkeletonLoader className="h-40 w-full" />
        <div className="p-4">
            <SkeletonLoader className="h-4 w-24 mb-2" />
            <SkeletonLoader className="h-6 mb-2" />
            <SkeletonLoader className="h-4 w-full mb-1" />
            <SkeletonLoader className="h-4 w-3/4" />
        </div>
    </div>
);

const HomePage = () => {
    const { t, language } = useLanguage();
    const [showModal, setShowModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [formData, setFormData] = useState({ name: '', phone: '' });
    const [submitted, setSubmitted] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loading, setLoading] = useState(true);

    // API'dan keladigan ma'lumotlar
    const [slides, setSlides] = useState([]);
    const [brands, setBrands] = useState([]);
    const [clients, setClients] = useState([]);
    const [news, setNews] = useState([]);
    const [productsData, setProductsData] = useState({ categories: [] });
    const [aboutData, setAboutData] = useState(null);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);

    // API'dan ma'lumotlarni olish
    useEffect(() => {
        fetchAllData();
    }, [language]);

    // Tilga qarab navigatsiya
    const navigate = useNavigate();

    const handleShowMore = (categoryId) => {
        navigate(`/shop?category=${categoryId}`);
    };

    const handleBrandClick = (brandId) => {
        navigate(`/shop?brand=${brandId}`);
    };

    const features = [
        {
            icon: '📦',
            title: t('home.order'),
            desc: t('home.orderDesc')
        },
        {
            icon: '📦',
            title: t('home.delivery'),
            desc: t('home.deliveryDesc')
        },
        {
            icon: '📦',
            title: t('home.warranty'),
            desc: t('home.warrantyDesc')
        },
        {
            icon: '📦',
            title: t('home.assortment'),
            desc: t('home.assortmentDesc')
        },
    ];

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [
                slidersRes,
                productsRes,
                categoriesRes,
                brandsRes,
                newsRes,
                clientsRes,
                aboutRes
            ] = await Promise.all([
                axios.get(`${API_BASE_URL}/sliders/`).catch(() => ({ data: [] })),
                axios.get(`${API_BASE_URL}/products/?main_cat=true&page_size=100`).catch(() => ({ data: [] })),  // ← 100 ta mahsulot (5 kategoriya × 12-20 ta)
                axios.get(`${API_BASE_URL}/categories/?main_cat=true`).catch(() => ({ data: [] })),  // ← OPTIMIZED: main_cat filter
                axios.get(`${API_BASE_URL}/brands/`).catch(() => ({ data: [] })),
                axios.get(`${API_BASE_URL}/news/`).catch(() => ({ data: [] })),
                axios.get(`${API_BASE_URL}/clients/`).catch(() => ({ data: [] })),
                axios.get(`${API_BASE_URL}/about/`).catch(() => ({ data: [] }))
            ]);

            // Sliders
            if (slidersRes.data && slidersRes.data.length > 0) {
                const formattedSlides = slidersRes.data.map(slide => ({
                    id: slide.id,
                    title: slide[`title_${language}`] || slide.title_uz || slide.title || '',
                    description: slide[`subtitle_${language}`] || slide.subtitle_uz || slide.subtitle || '',
                    button_text: slide[`button_text${language}`] || slide.button_text_uz || slide.button_text || '',
                    image: slide.image || 'https://via.placeholder.com/1200x400',
                    link: slide.button_link || '#'
                }));
                setSlides(formattedSlides);
            }

            // Products - kategoriyalarga ajratish
            // Pagination response: {count: X, results: [...]}
            const productsData = productsRes.data.results || productsRes.data;

            if (productsData && productsData.length > 0) {
                setProducts(productsData);

                // Kategoriyalar bo'yicha guruhlash (API'dan allaqachon main_cat=true kategoriyalar keldi)
                if (categoriesRes.data && categoriesRes.data.length > 0) {
                    const groupedProducts = categoriesRes.data.map(cat => ({
                        id: cat.id,
                        name: cat[`title_${language}`] || cat.title_uz || cat.title,
                        items: productsData  // ← productsRes.data o'rniga productsData
                            .filter(product =>
                                product.categories &&
                                product.categories.some(c => c.id === cat.id)
                            )
                            .slice(0, 12)  // Har kategoriyadagi faqat 12 ta mahsulot
                            .map(product => ({
                                id: product.id,
                                name: product[`name_${language}`] || product.name_uz || product.name,
                                image: product.main_image || 'https://via.placeholder.com/300',
                                code: product.code,
                                inStock: true,
                                discount: Math.random() > 0.7 ? Math.floor(Math.random() * 30) : null,
                                isNew: Math.random() > 0.8
                            }))
                    })).filter(cat => cat.items.length > 0);

                    setProductsData({ categories: groupedProducts });
                    setCategories(categoriesRes.data);
                }
            }

            // Brands
            if (brandsRes.data && brandsRes.data.length > 0) {
                setBrands(brandsRes.data.map(brand => ({
                    id: brand.id,
                    name: brand[`title_${language}`] || brand.title_uz || brand.title,
                    logo: brand["image"]
                })));
            }

            // News
            if (newsRes.data && newsRes.data.length > 0) {
                setNews(newsRes.data.slice(0, 5).map(item => {
                    const date = new Date(item.created_at);
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    return {
                        id: item.id,
                        title: item[`title_${language}`] || item.title_uz || item.title,
                        date: `${day}.${month}.${year}`,
                        category: item[`category_${language}`] || item.category_uz || item.category || t('home.news'),
                        excerpt: item[`excerpt_${language}`] || item.excerpt_uz || item.excerpt || '',
                        image: item.image || 'https://via.placeholder.com/300x200'
                    };
                }));
            }

            // Clients
            if (clientsRes.data && clientsRes.data.length > 0) {
                setClients(clientsRes.data.map(client => ({
                    name: client.title || 'Client',
                    logo: client.logo || 'https://via.placeholder.com/150x100',
                    url: client.url || '#'
                })));
            }

            // About
            if (aboutRes.data && aboutRes.data.length > 0) {
                const about = aboutRes.data[0];
                setAboutData({
                    ...about,
                    text: about[`text_${language}`] || about.text_uz || about.text
                });
            }

        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const stripHtml = (html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent || "";
    };

    // Auto-play slider
    useEffect(() => {
        if (slides.length > 1) {
            const timer = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % slides.length);
            }, 5000);
            return () => clearInterval(timer);
        }
    }, [slides.length, currentSlide]);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    };

    const handleProductClick = (product) => {
        setSelectedProduct(product);
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.phone || !selectedProduct) return;

        try {
            await axios.post(`${API_BASE_URL}/zayavka/`, {
                product: selectedProduct.id,
                name: formData.name,
                phone: formData.phone
            });

            setSubmitted(true);
            setTimeout(() => {
                setShowModal(false);
                setSubmitted(false);
                setFormData({ name: '', phone: '' });
                setSelectedProduct(null);
            }, 3000);
        } catch (error) {
            alert('Xatolik yuz berdi. Iltimos qayta urinib ko\'ring.');
        }
    };

    const helmetConfig = getHelmetConfig(language);
    const pageConfig = helmetConfig.home;

    return (
        <div className="min-h-screen bg-gray-50 pt-8">
            <Helmet>
                <title>{pageConfig.title}</title>
                <meta name="description" content={pageConfig.description} />
                <meta name="keywords" content={pageConfig.keywords} />
            </Helmet>
            {/* Hero Slider */}
            <section className="relative mx-2 sm:mx-4 lg:mx-auto lg:max-w-7xl py-4 sm:py-6 lg:px-4 min-h-[30vh] sm:min-h-[35vh] rounded-[10px] overflow-hidden">
                {loading ? (
                    <div className="h-full flex items-center justify-center">
                        <SkeletonLoader className="h-full w-full" />
                    </div>
                ) : slides.length > 0 ? (
                    <>
                        {slides.map((slide, index) => (
                            <div
                                key={index}
                                className={`absolute inset-0 transition-opacity duration-500 ${
                                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                                }`}
                            >
                                <img
                                    src={slide.image}
                                    alt={slide.title}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black/40" />
                            </div>
                        ))}

                        <div className="absolute inset-0 flex items-center">
                            <div className="container mx-auto px-4">
                                <div className="max-w-3xl text-white">
                                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-2 sm:mb-4">
                                        {slides[currentSlide]?.title}
                                    </h1>
                                    <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-8">
                                        {slides[currentSlide]?.description}
                                    </p>
                                    {slides[currentSlide]?.button_text && (
                                        <Link to={slides[currentSlide]?.link} className="mt-4 sm:mt-6 px-4 sm:px-8 py-2 sm:py-3 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition text-sm sm:text-base">
                                            {slides[currentSlide].button_text}
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Slider Controls */}
                        {slides.length > 1 && (
                            <>
                                <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/30 rounded-full hover:bg-white/50 transition">
                                    <ChevronLeft className="w-6 h-6 text-white" />
                                </button>
                                <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/30 rounded-full hover:bg-white/50 transition">
                                    <ChevronRight className="w-6 h-6 text-white" />
                                </button>

                                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                                    {slides.map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentSlide(index)}
                                            className={`w-3 h-3 rounded-full transition ${
                                                index === currentSlide ? 'bg-white' : 'bg-white/50'
                                            }`}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                ) : (
                    <div className="h-full flex items-center">
                    </div>
                )}
            </section>
            <div className="relative max-w-7xl mx-auto pt-4 py-6 lg:mt-4">
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {features.map((feature, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg border hover:shadow-lg transition">
                            <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                            <p className="text-sm text-gray-600">{feature.desc}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="relative max-w-7xl mx-auto pt-4 py-6">

                {/* Mahsulotlar */}
                <section className="mb-12">
                    {loading ? (
                        <div className="space-y-8">
                            {[...Array(2)].map((_, i) => (
                                <div key={i}>
                                    <SkeletonLoader className="h-8 w-48 mb-4" />
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                        {[...Array(4)].map((_, j) => (
                                            <ProductSkeleton key={j} />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : productsData.categories.length > 0 ? (
                        <div className="space-y-8">
                            {productsData.categories.map((category) => (
                                <div key={category.id}>
                                    <div className="flex items-center justify-between mb-4">
                                        <h4 className="text-xl font-semibold flex items-center gap-2">
                                            <span className="text-2xl">{category.icon}</span>
                                            {category.name}
                                        </h4>
                                        <button
                                            onClick={() => handleShowMore(category.id)}
                                            className="text-orange-600 hover:text-orange-800"
                                        >
                                            {t("home.viewAll")} →
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                        {category.items.slice(0, 4).map((product) => (

                                            <Link key={product.id} to={`/product/${product.id}`} className="bg-white rounded-lg shadow hover:shadow-lg transition cursor-pointer relative overflow-hidden">
                                                <img src={product.image} alt={product.name} className="w-full h-48 object-cover" />
                                                <div className="p-4">
                                                    <p className="text-sm text-gray-500 mb-1">{product.code}</p>
                                                    <h5 className="font-medium mb-3">{product.name}</h5>
                                                    <button className="w-full bg-orange-600 text-white py-2 rounded hover:bg-orange-700 transition">
                                                        {t("shop.sendRequest")}
                                                    </button>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </section>

                {/* Brendlar */}
                <section className="mb-12">
                    <h3 className="text-2xl font-bold mb-6">{t('home.brands')}</h3>
                    {loading ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4">
                            {[...Array(8)].map((_, i) => (
                                <SkeletonLoader key={i} className="h-20 rounded-lg" />
                            ))}
                        </div>
                    ) : brands.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4">
                            {brands.map((brand, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleBrandClick(brand.id)}
                                    className="bg-white p-4 rounded-lg border transition flex items-center justify-center cursor-pointer"
                                >
                                    <img src={brand.logo} alt={brand.name} className="max-w-full h-12 object-contain" />
                                </button>
                            ))}
                        </div>
                    ) : null}
                </section>

                {/* Yangiliklar */}
                <section className="mb-12">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-2xl font-bold">{t('home.newsSection')}</h3>
                        <Link to={"/blog"} className="text-orange-600 hover:text-orange-800">
                            {t('home.allNews')} →
                        </Link>
                    </div>
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                            {[...Array(5)].map((_, i) => (
                                <NewsSkeleton key={i} />
                            ))}
                        </div>
                    ) : news.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                            {news.map((item) => (
                                <Link key={item.id} to={`/blog/${item.id}`} className="group">
                                    <article className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition cursor-pointer group h-full">
                                        <div className="h-40 overflow-hidden">
                                            <img
                                                src={item.image}
                                                alt={item.title}
                                                className="w-full h-full object-cover group-hover:scale-110 transition duration-300"
                                            />
                                        </div>
                                        <div className="p-4">
                                            <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                                                <span>{item.date}</span>
                                                <span>•</span>
                                                <span className="text-orange-600">{item.category}</span>
                                            </div>
                                            <h4 className="font-medium mb-2 line-clamp-2">{item.title}</h4>
                                            <p className="text-sm text-gray-600 line-clamp-2">{item.excerpt}</p>
                                        </div>
                                    </article>
                                </Link>
                            ))}
                        </div>
                    ) : null}
                </section>

                {/* Biz haqimizda */}
                <section className="mb-12 bg-blue-50 rounded-lg p-12">
                    {loading ? (
                        <div className="flex items-start gap-8">
                            <SkeletonLoader className="w-32 h-32 rounded-lg flex-shrink-0" />
                            <div className="flex-1 space-y-4">
                                <SkeletonLoader className="h-8 w-3/4" />
                                <SkeletonLoader className="h-4 w-full" />
                                <SkeletonLoader className="h-4 w-full" />
                                <SkeletonLoader className="h-4 w-5/6" />
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-8">
                            <div className="w-24 sm:w-32 flex-shrink-0">
                                <div className="w-24 sm:w-32 h-24 sm:h-32 bg-blue-500 rounded-lg flex items-center justify-center bg-white">
                                    <img src={aboutData.logo} alt={aboutData.phone} className="max-w-full h-16 object-contain grayscale hover:grayscale-0 transition" />
                                </div>
                            </div>
                            <div>
                                <div className="space-y-4 text-gray-700 text-sm sm:text-base">
                                    {aboutData ? (
                                        <div dangerouslySetInnerHTML={{ __html: aboutData.text }} />
                                    ) : ''}
                                </div>
                            </div>
                        </div>
                    )}
                </section>

                {/* Bizning mijozlar */}
                <section className="mb-12">
                    <h3 className="text-2xl font-bold mb-6">{t('home.clients')}</h3>
                    {loading ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4 sm:gap-6">
                            {[...Array(7)].map((_, i) => (
                                <SkeletonLoader key={i} className="h-24 rounded-lg" />
                            ))}
                        </div>
                    ) : clients.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4 sm:gap-6">
                            {clients.map((client, index) => (
                                <a href={client.url} target={"_blank"} key={index} className="bg-white p-6 rounded-lg border transition flex items-center justify-center">
                                        <img src={client.logo} alt={client.name} className="max-w-full h-16 object-contain transition" />
                                </a>
                            ))}
                        </div>
                    ) : null}
                </section>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl max-w-md w-full p-6 relative">
                        <button
                            onClick={() => setShowModal(false)}
                            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                        >
                            <X size={24} />
                        </button>

                        {!submitted ? (
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">
                                    {t('modal.title')}
                                </h3>
                                <p className="text-sm text-gray-600 mb-1">{selectedProduct?.code}</p>
                                <p className="text-gray-800 mb-6 font-medium">
                                    {selectedProduct?.name}
                                </p>

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('modal.fullName')} *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('modal.phone')} * *
                                        </label>
                                        <input
                                            type="tel"
                                            required
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 rounded-lg transition"
                                    >
                                        {t('modal.send')}
                                    </button>
                                </form>
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <h4 className="text-xl font-bold text-gray-900 mb-2">
                                    {t('modal.successTitle')}
                                </h4>
                                <p className="text-gray-600">
                                    {t('modal.successMessage')}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default HomePage;